import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { ColumnDef } from "@tanstack/react-table"
import {
  ArrowPathIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FilmIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline"
import { DataTable } from "@/components/DataTable/DataTable"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog"
import { Button } from "@/components/Button"
import { Layout } from "../components/Layout"
import {
  RETRYABLE_STAGES,
  STAGE_STATUSES,
  useClipProcessingEvents,
  useClips,
  useRetryClipStage,
  type Clip,
  type RetryableStage,
  type StageStatus,
  type StatusFilter,
} from "@/hooks/useClips"
import {
  REAL_PLAY_MAX_SECONDS,
  clipDurationSeconds,
  effectiveStageStatus,
  latestStageEvent,
} from "@/lib/clipStageStatus"

// Client-side virtual/real-play filter over the already-loaded page (we do NOT add a
// backend query param — it filters within the fetched rows only).
type ClipKindFilter = "all" | "virtual" | "real"
const CLIP_KIND_FILTERS = ["all", "virtual", "real"] as const
const CLIP_KIND_LABELS: Record<ClipKindFilter, string> = {
  all: "All clips",
  virtual: "Virtual only",
  real: "Real plays",
}

const STAGE_STATUS_STYLES: Record<StageStatus, string> = {
  pending: "bg-gray-100 text-gray-700",
  running: "bg-blue-100 text-blue-800",
  succeeded: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  // Greyed-out + dashed outline so a stage that will never run reads clearly as "not
  // applicable" rather than blending into the (solid) pending badge.
  skipped: "bg-transparent text-gray-400 border border-dashed border-gray-300",
}

function StageBadge({ status }: { status: StageStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STAGE_STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  )
}

// D1 stores timestamps as "YYYY-MM-DD HH:MM:SS" — UTC but with NO timezone marker, which
// `new Date()` would parse as LOCAL time (making every row look hours in the future →
// clamped to "now"). Normalize to ISO-UTC ("...THH:MM:SSZ") so it parses correctly.
function parseUtc(value: string | null): number {
  if (!value) return NaN
  // Already ISO with a zone? use as-is; else treat the space-separated D1 form as UTC.
  const iso = /[T].*(Z|[+-]\d\d:?\d\d)$/.test(value) ? value : value.replace(" ", "T") + "Z"
  return new Date(iso).getTime()
}

function formatAge(value: string | null): string {
  const timestamp = parseUtc(value)
  if (Number.isNaN(timestamp)) return "—"

  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000))
  if (seconds < 10) return "now"
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// Exact local date+time for a D1 (UTC) timestamp — used for tooltips and the drill-down.
function formatAbsolute(value: string | null): string {
  const timestamp = parseUtc(value)
  if (Number.isNaN(timestamp)) return "—"
  return new Date(timestamp).toLocaleString()
}

// Clip length = end_seconds - start_seconds. "—" for a whole-file clip (start/end null).
function formatLength(clip: { start_seconds: number | null; end_seconds: number | null }): string {
  const duration = clipDurationSeconds(clip)
  if (duration == null) return "—"
  return `${duration.toFixed(1)}s`
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: number | string
  icon: React.ReactNode
}) {
  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 text-secondary text-sm mb-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-primary">{value}</div>
    </div>
  )
}

function ToggleFilter<T extends string>({
  options,
  value,
  onChange,
  labelFor,
}: {
  options: readonly T[]
  value: T
  onChange: (value: T) => void
  labelFor: (value: T) => string
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-background p-1">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={`h-8 min-w-[72px] rounded px-3 text-sm font-medium transition-colors ${
            value === option ? "bg-primary text-contrast" : "text-secondary hover:text-primary"
          }`}
        >
          {labelFor(option)}
        </button>
      ))}
    </div>
  )
}

// The drill-down: one clip's append-only attempt timeline + per-stage retry.
function ClipTimelineDialog({ clip, onClose }: { clip: Clip | null; onClose: () => void }) {
  const events = useClipProcessingEvents(clip?.public_id ?? null)
  const retry = useRetryClipStage()
  // Keep accepted attempts across closing/reopening the dialog and stale polls.
  // An older failure must not unlock a retry that the server has just accepted.
  const [acceptedAttempts, setAcceptedAttempts] = useState<Record<string, number>>({})
  const stageState = (stage: RetryableStage) => {
    const event = latestStageEvent(stage, events.data ?? [])
    const accepted = acceptedAttempts[`${clip?.public_id}:${stage}`] ?? 0
    if (accepted > (event?.attempt ?? 0)) return { status: "pending" as const, at: null }
    return {
      status: event?.status ?? (clip ? effectiveStageStatus(stage, clip) : "pending"),
      at: event?.created_at ?? clip?.[`${stage}_updated_at`] ?? null,
    }
  }

  return (
    <Dialog open={!!clip} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{clip?.public_id}</DialogTitle>
          <DialogDescription>
            Processing timeline — retry re-dispatches a stage to the workers and resets it to
            pending.
          </DialogDescription>
        </DialogHeader>

        {clip && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {/* Retry is offered only for the columned, retryable stages (transcode/cover/ai).
                  `fragment` is observability-only and the backend rejects a fragment retry, so it
                  must never get a retry button here. */}
              {RETRYABLE_STAGES.map((stage: RetryableStage) => {
                const { status } = stageState(stage)
                const submitting = retry.isPending && retry.variables?.publicId === clip.public_id &&
                  retry.variables.stage === stage
                const busy = status === "pending" || status === "running"
                return (
                  <Button
                    key={stage}
                    variant="outline"
                    size="sm"
                    disabled={retry.isPending || busy || events.isLoading || events.isError}
                    onClick={() => retry.mutate({ publicId: clip.public_id, stage }, {
                      onSuccess: (data) => setAcceptedAttempts((previous) => ({
                        ...previous,
                        [`${clip.public_id}:${stage}`]: data.attempt,
                      })),
                    })}
                  >
                    <ArrowPathIcon className={`h-3.5 w-3.5${submitting || status === "running" ? " animate-spin" : ""}`} />
                    {submitting ? `Retrying ${stage}…` : busy
                      ? `${stage}: ${status === "pending" ? "Queued" : "Running"}`
                      : `Retry ${stage}`}
                  </Button>
                )
              })}
            </div>
            {retry.isError && (
              <div className="text-sm text-red-400">
                Retry failed: {(retry.error as Error)?.message}
              </div>
            )}
            {events.isError && (
              <div role="alert" className="text-sm text-red-400">
                Could not refresh processing status. Retry controls will return when status is available.
              </div>
            )}

            {/* Per-stage outcome + when it finished (hover for the exact local time).
                Uses the same derived (effective) status as the table so a never-run stage
                reads "skipped", not "pending". */}
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ["transcode", clip.transcode_updated_at],
                  ["cover", clip.cover_updated_at],
                  ["ai", clip.ai_updated_at],
                ] as const
              ).map(([label, at]) => {
                const { status: st, at: latestAt } = stageState(label)
                return (
                  <div key={label} className="rounded-md border border-border p-2">
                    <div className="text-xs uppercase tracking-wide text-secondary">{label}</div>
                    <div className="mt-1">
                      <StageBadge status={st} />
                    </div>
                    <div className="mt-1 text-xs text-secondary" title={formatAbsolute(latestAt ?? at)}>
                      {st === "pending" || st === "running" || st === "skipped"
                        ? "—"
                        : `${st === "succeeded" ? "finished" : st} ${formatAge(latestAt ?? at)}`}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Raw timestamps, labeled, for debugging the effective-time ordering.
                created_at is copied from the parent onto virtual children by /split;
                sliced_at is the real row-creation time (null for whole-file/legacy rows).
                The list's single "Created" column shows the EFFECTIVE time (sliced_at ??
                created_at) — this block surfaces both so the distinction is visible. */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-md border border-border p-2">
                <div className="uppercase tracking-wide text-secondary">created_at</div>
                <div className="mt-1 text-primary">{formatAbsolute(clip.created_at)}</div>
                <div className="text-secondary">{formatAge(clip.created_at)}</div>
              </div>
              <div className="rounded-md border border-border p-2">
                <div className="uppercase tracking-wide text-secondary">sliced_at</div>
                <div className="mt-1 text-primary">
                  {clip.sliced_at ? formatAbsolute(clip.sliced_at) : "— (whole-file / legacy)"}
                </div>
                <div className="text-secondary">
                  {clip.sliced_at ? formatAge(clip.sliced_at) : ""}
                </div>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto rounded-lg border border-border">
              {events.isLoading ? (
                <div className="p-4 text-sm text-secondary">Loading timeline…</div>
              ) : !events.data || events.data.length === 0 ? (
                <div className="p-4 text-sm text-secondary">
                  No processing events yet for this clip.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-secondary">
                    <tr>
                      <th className="px-3 py-2">Stage</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Attempt</th>
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.data.map((event, i) => (
                      <tr key={i} className="border-t border-border align-top">
                        <td className="px-3 py-2 text-primary">{event.stage}</td>
                        <td className="px-3 py-2">
                          <StageBadge status={event.status} />
                        </td>
                        <td className="px-3 py-2 text-primary">{event.attempt}</td>
                        <td
                          className="px-3 py-2 text-secondary"
                          title={formatAbsolute(event.created_at)}
                        >
                          {formatAge(event.created_at)}
                        </td>
                        <td className="px-3 py-2">
                          {event.error ? (
                            <span className="text-red-400" title={event.error}>
                              {event.error}
                            </span>
                          ) : event.detail ? (
                            <span
                              className="text-secondary"
                              title={JSON.stringify(event.detail)}
                            >
                              {event.duration_ms != null ? `${event.duration_ms}ms · ` : ""}
                              {JSON.stringify(event.detail).slice(0, 80)}
                            </span>
                          ) : event.duration_ms != null ? (
                            <span className="text-secondary">{event.duration_ms}ms</span>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function Clips() {
  const [status, setStatus] = useState<StatusFilter>("all")
  // The Clips page shows per-clip health for the columned/retryable stages only, so its
  // stage filter never offers `fragment` (observability-only; surfaced in Recent Operations).
  const [stage, setStage] = useState<"all" | RetryableStage>("all")
  // Hydrate the game filter from the URL (?game=g.…) so deep links from other admin pages
  // (e.g. the Recent Operations clip link) open already scoped to that game.
  const [searchParams] = useSearchParams()
  const [game, setGame] = useState(() => searchParams.get("game") ?? "")
  const [kind, setKind] = useState<ClipKindFilter>("all")
  const [selected, setSelected] = useState<Clip | null>(null)

  const { data, isLoading, error, dataUpdatedAt } = useClips({ status, stage, game })

  const allClips = useMemo(() => data ?? [], [data])

  // Client-side virtual/real-play filter applied WITHIN the already-fetched page (no
  // backend query param). "Virtual" = a bounded clip (start_seconds != null); "Real
  // plays" = virtual AND inside the real-play window (≤ REAL_PLAY_MAX_SECONDS).
  const clips = useMemo(() => {
    if (kind === "all") return allClips
    return allClips.filter((clip) => {
      const isVirtual = clip.start_seconds != null
      if (kind === "virtual") return isVirtual
      // "real": virtual and ≤40s (a known duration inside the window).
      const duration = clipDurationSeconds(clip)
      return isVirtual && duration != null && duration <= REAL_PLAY_MAX_SECONDS
    })
  }, [allClips, kind])

  const stats = useMemo(() => {
    return clips.reduce(
      (acc, clip) => {
        acc.total += 1
        const statuses = [clip.transcode_status, clip.cover_status, clip.ai_status]
        if (statuses.includes("failed")) acc.failed += 1
        if (statuses.includes("running")) acc.running += 1
        return acc
      },
      { total: 0, failed: 0, running: 0 }
    )
  }, [clips])

  const columns = useMemo<ColumnDef<Clip>[]>(
    () => [
      {
        accessorKey: "public_id",
        header: "Clip",
        cell: ({ row }) => (
          <span className="font-mono text-sm text-primary">{row.original.public_id}</span>
        ),
      },
      {
        accessorKey: "game_public_id",
        header: "Game",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-secondary">
            {row.original.game_public_id ?? "—"}
          </span>
        ),
      },
      {
        // Length = end_seconds - start_seconds; "—" for a whole-file clip. Placed next to
        // the stage columns since it's what drives the cover/AI "skipped" derivation below.
        id: "length",
        header: "Length",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">{formatLength(row.original)}</span>
        ),
      },
      {
        accessorKey: "transcode_status",
        header: "Transcode",
        cell: ({ row }) => <StageBadge status={effectiveStageStatus("transcode", row.original)} />,
      },
      {
        accessorKey: "cover_status",
        header: "Cover",
        cell: ({ row }) => <StageBadge status={effectiveStageStatus("cover", row.original)} />,
      },
      {
        accessorKey: "ai_status",
        header: "AI",
        cell: ({ row }) => <StageBadge status={effectiveStageStatus("ai", row.original)} />,
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => {
          // Effective row-creation time: /split copies the parent's created_at onto virtual
          // children, so a fresh slice would otherwise show the parent's old age. sliced_at
          // is the real row-creation time when set (null for whole-file/legacy rows).
          const effective = row.original.sliced_at ?? row.original.created_at
          return (
            <span className="text-sm text-secondary" title={formatAbsolute(effective)}>
              {formatAge(effective)}
            </span>
          )
        },
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Clips</h1>
            <p className="text-secondary">
              Per-clip processing health (transcode · cover · AI) — most recent 200; filter by game
              to narrow
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={game}
                onChange={(e) => setGame(e.target.value)}
                placeholder="Filter by game id (g.…)"
                className="h-8 w-44 rounded-md border border-border bg-background px-3 text-sm text-primary placeholder:text-secondary"
              />
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as StatusFilter)}
                className="h-8 rounded-md border border-border bg-background px-2 text-sm text-primary"
                aria-label="Filter by status"
              >
                <option value="all">All statuses</option>
                {STAGE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <ToggleFilter
                options={["all", ...RETRYABLE_STAGES] as const}
                value={stage}
                onChange={setStage}
                labelFor={(value) => (value === "all" ? "All" : value)}
              />
              {/* Client-side virtual/real-play filter over the loaded page only. */}
              <ToggleFilter
                options={CLIP_KIND_FILTERS}
                value={kind}
                onChange={setKind}
                labelFor={(value) => CLIP_KIND_LABELS[value]}
              />
            </div>
            <div className="text-xs text-secondary">
              Updated {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : "—"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Failed" value={stats.failed} icon={<XCircleIcon className="h-4 w-4" />} />
          <StatCard
            label="Running"
            value={stats.running}
            icon={<ClockIcon className="h-4 w-4" />}
          />
          <StatCard label="Showing" value={stats.total} icon={<FilmIcon className="h-4 w-4" />} />
        </div>

        {stage !== "all" && status !== "all" && (
          <div className="text-xs text-secondary">
            Showing clips where <span className="text-primary">{stage}</span> is{" "}
            <span className="text-primary">{status}</span>. Status alone matches any stage; the
            stage filter only narrows a status.
          </div>
        )}

        {kind !== "all" && (
          <div className="text-xs text-secondary">
            Showing <span className="text-primary">{CLIP_KIND_LABELS[kind].toLowerCase()}</span> —
            filtered client-side within the loaded page (not a server query).
          </div>
        )}

        {isLoading ? (
          <TableSkeleton />
        ) : clips.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface p-6 text-secondary">
            <ExclamationTriangleIcon className="h-5 w-5" />
            No clips match these filters.
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={clips}
            showToolbar={false}
            onRowClick={(clip) => setSelected(clip)}
          />
        )}
      </div>

      <ClipTimelineDialog
        clip={allClips.find((clip) => clip.public_id === selected?.public_id) ?? selected}
        onClose={() => setSelected(null)}
      />
    </Layout>
  )
}
